import type { Database } from "@/lib/database.types";
import type { ItemCategory, ItemRequest } from "@/lib/types";
import { normalizePreferenceAttrs } from "./preferenceAttrs";

type SupabaseItemRequest = Database["public"]["Tables"]["item_requests"]["Row"];

/**
 * Map DB row → domain ItemRequest.
 * Owner: `src/lib/item-requests/` (single mapper for API + client).
 */
export function mapSupabaseItemRequestToItemRequest(
  request: SupabaseItemRequest,
): ItemRequest {
  if (!request.title || !request.user_id) {
    throw new Error("Invalid item request: missing required fields");
  }

  const description = request.description || "";
  const userLocation = request.user_location || "";

  const status = (request.status || "active") as
    | "active"
    | "fulfilled"
    | "expired";
  if (!["active", "fulfilled", "expired"].includes(status)) {
    throw new Error(`Invalid status: ${status}`);
  }

  const urgency = (request.urgency || "medium") as "low" | "medium" | "high";
  if (!["low", "medium", "high"].includes(urgency)) {
    throw new Error(`Invalid urgency: ${urgency}`);
  }

  const category = request.category as ItemCategory;
  if (!category) {
    throw new Error("Invalid category: category is required");
  }

  const reviewStatus = request.review_status
    ? (request.review_status as "pending" | "approved" | "rejected")
    : undefined;

  const createdAt = request.created_at
    ? new Date(request.created_at)
    : new Date();
  const updatedAt = request.updated_at
    ? new Date(request.updated_at)
    : new Date();
  const expiresAt = request.expires_at
    ? new Date(request.expires_at)
    : undefined;

  const approvedVia =
    request.approved_via === "auto" || request.approved_via === "admin"
      ? request.approved_via
      : undefined;

  const preferenceAttrs = normalizePreferenceAttrs(
    request.preference_attrs ?? {},
  );

  return {
    id: request.id,
    title: request.title,
    description,
    category,
    subcategory: request.subcategory || undefined,
    maxPrice: request.max_price || undefined,
    userLocation,
    userId: request.user_id,
    status,
    reviewStatus,
    approvedVia,
    urgency,
    preferenceAttrs:
      Object.keys(preferenceAttrs).length > 0 ? preferenceAttrs : undefined,
    createdAt,
    updatedAt,
    expiresAt,
  };
}
